import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import SmsIcon from '@mui/icons-material/Sms';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import CancelIcon from '@mui/icons-material/Cancel';

import { GET_CUSTOMER_THEFT_REGISTRY, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../core/constants';
import { getTheftRegistryStatus, getTheftRegistryStatusColor } from '../../common/utils/theft_registry_utils';
import { TheftRegistryStatusEnum } from '../../../core/enums/order/theft_registry';
import { CustomIconButton } from '../../common/button';
import { getAllPodsState } from '../../../features/pod/get_all_pods';
import { getTheftRegistriesForStoreState } from '../../../features/order/get_theft_registries_for_store';
import { updateTheftRegistryStatusAction } from '../../../features/order/update_theft_registry_status';
import { cancelTheftRegistryNotificationData, createTheftRegistrySmsNotificationData, updateTheftRegistryStatusNotificationData } from '../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import useNotification from '../../../core/hooks/use_notification';
import ConfirmDialog from '../common/confirm_dialog';
import { createTheftSmsAction } from '../../../features/order/create_theft_sms';
import { cancelTheftRegistryAction } from '../../../features/order/cancel_theft_registry';

const TheftRegistriesList = ({ queryParams, refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const getTheftRegistriesForStore = useSelector(getTheftRegistriesForStoreState);
    const getAllPods = useSelector(getAllPodsState);

    const [loadingStatusIds, setLoadingStatusIds] = useState([]);
    const [loadingCancelIds, setLoadingCancelIds] = useState([]);
    const [loadingSmsIds, setLoadingSmsIds] = useState([]);

    const onUpdateTheftRegistryStatus = (id) => {
        setLoadingStatusIds(prev => [...prev, id]);
        dispatch(updateTheftRegistryStatusAction({ id, status: TheftRegistryStatusEnum.FINISHED.value }))
            .then(response => {
                showNotification(updateTheftRegistryStatusNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) refreshList();
            })
            .finally(() => {
                setLoadingStatusIds(prev => prev.filter(itemId => itemId !== id));
            });
    };

    const onCancelTheftRegistry = (id) => {
        setLoadingCancelIds(prev => [...prev, id]);
        dispatch(cancelTheftRegistryAction(id))
            .then(response => {
                showNotification(cancelTheftRegistryNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) refreshList();
            })
            .finally(() => {
                setLoadingCancelIds(prev => prev.filter(itemId => itemId !== id));
            });
    };

    const onSendSms = (id) => {
        setLoadingSmsIds(prev => [...prev, id]);
        dispatch(createTheftSmsAction(id))
            .then(response => showNotification(createTheftRegistrySmsNotificationData(response)))
            .finally(() => {
                setLoadingSmsIds(prev => prev.filter(itemId => itemId !== id));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>ID</TableCell>
                        <TableCell>Data kradzieży</TableCell>
                        <TableCell>Punkt sprzedaży</TableCell>
                        <TableCell align="center">ID klienta</TableCell>
                        <TableCell align="right">Wartość</TableCell>
                        <TableCell align="center">Częściowa kradzież</TableCell>
                        <TableCell align="center">Wysłano SMS</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right" sx={ { minWidth: 160 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getTheftRegistriesForStore?.data?.content?.map(theftRegistry => (
                            <TableRow key={ theftRegistry?.id } hover>
                                <TableCell sx={ { fontWeight: 600 } }>
                                    T{ theftRegistry?.id }
                                </TableCell>
                                <TableCell>
                                    { moment(theftRegistry?.theftDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={ { maxWidth: 200 } } noWrap title={
                                        getAllPods?.data?.map(pod => ({
                                            id: pod?.id,
                                            label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                                        }))
                                            .find(pod => pod?.id === theftRegistry?.podId)?.label || 'Brak'
                                    }>
                                        { getAllPods?.data?.map(pod => ({
                                            id: pod?.id,
                                            label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                                        }))
                                            .find(pod => pod?.id === theftRegistry?.podId)?.label || 'Brak' }
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">{ theftRegistry?.customerId }</TableCell>
                                <TableCell align="right" sx={ { fontWeight: 500 } }>
                                    { theftRegistry?.shopAdminTotalSellingPrice.toFixed(2) } { theftRegistry?.currency }
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={ theftRegistry?.partialTheft ? "TAK" : "NIE" }
                                        color="secondary"
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={ theftRegistry?.smsSent ? `TAK (${ theftRegistry?.smsSentCount })` : "NIE" }
                                        color={ theftRegistry?.smsSent ? "success" : "default" }
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={ getTheftRegistryStatus(theftRegistry?.status) }
                                        color={ getTheftRegistryStatusColor(theftRegistry?.status) }
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" justifyContent="flex-end" spacing={ 1 }>
                                        { theftRegistry?.status === TheftRegistryStatusEnum.IN_REALIZATION.value && (
                                            <>
                                                <ConfirmDialog
                                                    isLoading={ loadingSmsIds.includes(theftRegistry?.id) }
                                                    maxWidth="sm"
                                                    title={ theftRegistry?.smsSent ? 'Czy na pewno chcesz ponownie wysłać wiadomość sms?' : 'Czy na pewno chcesz wysłać wiadomość sms?' }
                                                    secondaryAction={ () => {
                                                    } }
                                                    secondaryText="Anuluj"
                                                    primaryAction={ () => onSendSms(theftRegistry?.id) }
                                                    primaryText="Wyślij"
                                                    displayIcon
                                                    iconTitle="Wyślij SMS"
                                                    icon={ <SmsIcon fontSize="small"/> }
                                                />
                                                <CustomIconButton
                                                    title="Opłacono"
                                                    isLoading={ loadingStatusIds.includes(theftRegistry?.id) }
                                                    onClick={ () => onUpdateTheftRegistryStatus(theftRegistry?.id) }
                                                    icon={ <CreditScoreIcon fontSize="small"/> }
                                                />
                                                <CustomIconButton
                                                    title="Anuluj"
                                                    isLoading={ loadingCancelIds.includes(theftRegistry?.id) }
                                                    onClick={ () => onCancelTheftRegistry(theftRegistry?.id) }
                                                    icon={ <CancelIcon fontSize="small"/> }
                                                />
                                            </>
                                        ) }
                                        <Tooltip title="Szczegóły">
                                            <IconButton
                                                size="small"
                                                onClick={ () => navigate(GET_CUSTOMER_THEFT_REGISTRY(theftRegistry?.customerId, theftRegistry?.id),
                                                    {
                                                        state: {
                                                            source: 'TheftRegistriesList',
                                                            queryParams,
                                                            incompleteOrderId: theftRegistry?.incompleteOrderId,
                                                            theftDate: theftRegistry?.theftDate,
                                                            podId: theftRegistry?.podId
                                                        }
                                                    }) }
                                            >
                                                <SearchIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default TheftRegistriesList;